import { useEffect, useState } from 'react';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import { ConfirmDialog } from './ConfirmDialog';
import { UserAvatar } from '../molecules/UserAvatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { useAuthStore } from '@/lib/stores/auth';
import { useProfileQuery, useSignOutMutation } from '@/features/settings/queries';
import { roleBadgeLabel, PROFILE_SETTINGS_SECTION } from './sidebar-helpers';
import { LogOut } from '@/icons/Icon';
import { syncBetterBugsIdentity } from '@/lib/betterbugs';

/**
 * The bottom-of-rail user row: avatar + name + "Role · email", which opens
 * Profile settings, and a sign-out icon button. Self-contained — it owns the
 * profile, role, feedback-widget identity, and sign-out wiring so the
 * `Sidebar` shell stays thin.
 */
export function SidebarUserRow({
  collapsed,
  userName,
  userEmail,
}: {
  collapsed: boolean;
  userName?: string;
  userEmail?: string;
}) {
  const navigate = useNavigate();
  const router = useRouter();

  const signOutMutation = useSignOutMutation();
  const signingOut = signOutMutation.isPending;
  const [confirmOpen, setConfirmOpen] = useState(false);

  const displayName = userName?.trim() || 'Account';
  const displayEmail = userEmail?.trim() || '';
  const avatarInitial = (displayName[0] ?? 'A').toUpperCase();

  const profile = useProfileQuery().data ?? null;
  const user = useAuthStore((s) => s.user);
  const organization = useAuthStore((s) => s.organization);
  const workspaceId = useAuthStore((s) => s.workspace?.id ?? null);

  // Third-party widget identity sync (BetterBugs) — re-runs on login, a user
  // swap on the same tab, and a workspace switch. Mounting it here keeps the
  // SDK off logged-out screens and starts its rewind capture for the
  // authenticated session.
  const sessionEmail = user?.email;
  const userId = user?.id ?? null;
  const organizationId = organization?.id ?? null;
  useEffect(() => {
    if (sessionEmail == null) return;
    syncBetterBugsIdentity({ email: sessionEmail, userId, organizationId, workspaceId });
  }, [sessionEmail, userId, organizationId, workspaceId]);

  // `profile.role` (not the Admin+-gated members list) is the current user's
  // role in the current workspace — every role can read their own profile.
  const roleLabel = roleBadgeLabel(profile?.role ?? null);

  const prefetchSettings = () => {
    void router.preloadRoute({ to: '/settings' }).catch(() => {});
  };
  const openProfile = () =>
    void navigate({ to: '/settings', search: { section: PROFILE_SETTINGS_SECTION } });

  async function handleSignOut() {
    try {
      // Route through the mutation (not `signOutAndClear` directly) so the
      // TanStack Query cache is wiped on sign-out. Without `qc.clear()`, the
      // next user who logs in on this tab is served the previous tenant's
      // cached data until each query refetches.
      await signOutMutation.mutateAsync();
    } finally {
      setConfirmOpen(false);
      void navigate({ to: '/login', replace: true });
    }
  }

  const avatar = (
    <UserAvatar
      name={avatarInitial}
      src={profile?.avatarUrl ?? user?.avatarUrl}
      fallback="silhouette"
      className="h-7 w-7"
      fallbackClassName="text-[11px]"
    />
  );

  const signOutButton = (
    <button
      type="button"
      aria-label={signingOut ? 'Signing out…' : 'Sign out'}
      title="Sign out"
      disabled={signingOut}
      onClick={() => setConfirmOpen(true)}
      className={cn('sidebar-signout', collapsed && 'is-collapsed')}
    >
      <LogOut size={collapsed ? 15 : 16} />
    </button>
  );

  const confirmDialog = (
    <ConfirmDialog
      open={confirmOpen}
      onOpenChange={(next) => {
        if (!next && !signingOut) setConfirmOpen(false);
      }}
      title="Log out"
      description="You'll be signed out of TrulyInbox on this device and will need to sign in again to get back in."
      cancelLabel="Cancel"
      confirmLabel="Log out"
      loadingLabel="Logging out…"
      cancelVariant="secondary"
      compactActions
      contentClassName="[&>button]:mt-2"
      loading={signingOut}
      onConfirm={() => void handleSignOut()}
    />
  );

  if (collapsed) {
    return (
      <div className="sidebar-user is-collapsed">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={`Profile settings for ${displayName}`}
              onClick={openProfile}
              onMouseEnter={prefetchSettings}
              onFocus={prefetchSettings}
              className="rounded-full"
            >
              {avatar}
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={10}>
            {roleLabel ? `${displayName} · ${roleLabel}` : displayName}
          </TooltipContent>
        </Tooltip>
        {signOutButton}
        {confirmDialog}
      </div>
    );
  }

  return (
    <div className="sidebar-user">
      <button
        type="button"
        onClick={openProfile}
        onMouseEnter={prefetchSettings}
        onFocus={prefetchSettings}
        className="sidebar-user-profile"
        title="Profile settings"
      >
        {avatar}
        <span className="min-w-0 flex-1">
          <span className="sidebar-user-name truncate" title={displayName}>
            {displayName}
          </span>
          <span className="sidebar-user-meta truncate">
            {roleLabel && <span>{roleLabel}</span>}
            {roleLabel && displayEmail && ' · '}
            {displayEmail && <span title={displayEmail}>{displayEmail}</span>}
          </span>
        </span>
      </button>
      {signOutButton}
      {confirmDialog}
    </div>
  );
}
